import { useState } from "react";
import { useScrollFadeIn } from "@/hooks/useScrollFadeIn";

const EMAIL = "hatomugicha91@gmail.com";

export default function ContactSection() {
  const { ref: headRef, style: headStyle } = useScrollFadeIn<HTMLDivElement>();
  const { ref: bodyRef, style: bodyStyle } = useScrollFadeIn<HTMLDivElement>({ delay: 0.15 });
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback for older browsers
      const el = document.createElement("textarea");
      el.value = EMAIL;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-20 bg-stone-50">
      <div className="max-w-3xl mx-auto px-4 md:px-10">

        {/* Heading */}
        <div ref={headRef} style={headStyle} className="text-center mb-5 md:mb-10">
          <p className="text-xs tracking-[0.4em] text-amber-700 uppercase mb-2 md:mb-3">Reservation</p>
          <h2 className="text-3xl md:text-4xl font-normal text-stone-800 tracking-widest">
            ご予約
          </h2>
          <div className="w-12 h-0.5 bg-amber-400 mx-auto mt-4 md:mt-5"></div>
        </div>

        {/* Body */}
        <div ref={bodyRef} style={bodyStyle}>

          {/* ひと言 */}
          <p className="text-center text-sm text-stone-500 tracking-wider mb-4 leading-relaxed">
            はじめての方も、お気軽にご連絡ください。
          </p>

          {/* 2カード縦並び（モバイル）/ 横並び（sm以上） */}
          <div className="flex flex-col sm:flex-row gap-3 mb-5 md:mb-8">

            {/* X DM Card */}
            <div className="flex-1 flex flex-col gap-2">
              <a
                href="https://x.com/mazo_kawaii"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center gap-4 px-5 py-6 bg-white border-2 border-stone-300 active:bg-stone-50 hover:border-stone-500 hover:bg-stone-50 transition-all duration-200 cursor-pointer group rounded-2xl"
              >
                <div className="w-13 h-13 w-[52px] h-[52px] flex items-center justify-center bg-stone-800 group-hover:bg-stone-700 transition-colors rounded-full flex-shrink-0">
                  <i className="ri-twitter-x-line text-white text-xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-bold text-stone-800 tracking-wide">XのDMで予約</p>
                  <p className="text-xs text-stone-400 mt-0.5">@mazo_kawaii</p>
                </div>
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-stone-100 group-hover:bg-stone-800 transition-colors flex-shrink-0">
                  <i className="ri-arrow-right-line text-stone-500 group-hover:text-white transition-colors text-sm"></i>
                </div>
              </a>
              {/* X 検索ヒント */}
              <div className="flex items-center gap-2 px-3 py-2 bg-stone-100 rounded-xl">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-search-line text-stone-400 text-xs"></i>
                </div>
                <p className="text-xs text-stone-500 leading-snug">
                  開けない場合は<span className="font-semibold text-stone-700">むぎ茶　@mazo_kawaii</span>で検索
                </p>
              </div>
            </div>

            {/* Email Card */}
            <div className="flex-1 flex flex-col gap-2">
              <a
                href={`mailto:${EMAIL}`}
                className="flex items-center gap-4 px-5 py-6 bg-amber-50 border-2 border-amber-300 active:bg-amber-100 hover:border-amber-500 hover:bg-amber-100 transition-all duration-200 cursor-pointer group rounded-2xl"
              >
                <div className="w-[52px] h-[52px] flex items-center justify-center bg-amber-600 group-hover:bg-amber-500 transition-colors rounded-full flex-shrink-0">
                  <i className="ri-mail-line text-white text-xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-bold text-stone-800 tracking-wide">メールで予約</p>
                  <p className="text-xs text-stone-400 mt-0.5 break-all">{EMAIL}</p>
                </div>
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-200 group-hover:bg-amber-600 transition-colors flex-shrink-0">
                  <i className="ri-arrow-right-line text-amber-600 group-hover:text-white transition-colors text-sm"></i>
                </div>
              </a>
              {/* メールアドレスコピー */}
              <button
                type="button"
                onClick={handleCopyEmail}
                className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 hover:bg-amber-100 active:bg-amber-200 transition-all duration-150 rounded-xl cursor-pointer w-full"
              >
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  {copied
                    ? <i className="ri-check-line text-amber-600 text-xs"></i>
                    : <i className="ri-file-copy-line text-amber-500 text-xs"></i>
                  }
                </div>
                <p className="text-xs text-amber-700 leading-snug text-left flex-1 min-w-0 break-all">
                  {copied
                    ? <span className="font-semibold">コピーしました！</span>
                    : <>開けない時はこちらで<span className="font-semibold"> {EMAIL} </span>コピーしてメール送信してください</>
                  }
                </p>
              </button>
            </div>
          </div>

          {/* 共通：記入項目 */}
          <div className="bg-white border border-stone-200 px-4 py-4 mb-4 md:mb-6 rounded-xl">
            <p className="text-xs text-stone-500 mb-3 tracking-wider">ご連絡の際に以下をお知らせください</p>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 flex items-center justify-center bg-amber-50 rounded-full flex-shrink-0">
                  <i className="ri-user-line text-amber-600 text-xs"></i>
                </div>
                <span className="text-sm text-stone-600">お名前</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center bg-amber-50 rounded-full flex-shrink-0 mt-0.5">
                  <i className="ri-calendar-line text-amber-600 text-xs"></i>
                </div>
                <div>
                  <span className="text-sm text-stone-600">希望日時</span>
                  <span className="text-xs text-stone-400 ml-2">（例：6月15日 14:00〜）</span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center bg-amber-50 rounded-full flex-shrink-0 mt-0.5">
                  <i className="ri-time-line text-amber-600 text-xs"></i>
                </div>
                <div>
                  <span className="text-sm text-stone-600">コース時間</span>
                  <span className="text-xs text-stone-400 ml-2">（例：90分）</span>
                </div>
              </div>
            </div>
          </div>

          {/* 注意事項 */}
          <div className="border border-stone-200 bg-stone-50 overflow-hidden rounded-xl">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-stone-100 border-b border-stone-200">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-stone-500 text-sm"></i>
              </div>
              <p className="text-xs tracking-[0.12em] text-stone-500 font-medium">ご予約前にお読みください</p>
            </div>
            <div className="divide-y divide-stone-100">
              <div className="flex items-start gap-3 px-4 py-3.5">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-mail-send-line text-stone-400 text-xs"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-stone-600 mb-1">返信メールについて</p>
                  <p className="text-xs text-stone-400 leading-[1.8]">
                    <span className="text-stone-500 font-medium">hatomugicha91@gmail.com</span> よりご返信します。迷惑メールフォルダもご確認ください。
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 px-4 py-3.5">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-error-warning-line text-stone-400 text-xs"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-stone-600 mb-1">キャンセルについて</p>
                  <p className="text-xs text-stone-400 leading-[1.8]">
                    無断当日キャンセルは<span className="text-stone-500 font-medium">プレイ料金の全額を請求</span>いたします。<br />お支払い未確認の場合、以降のご予約はお受けできません。
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
